import { usePatchCard, usePrivateCustomerCard, usePrivateCustomerCardPlastics } from 'api/react-query'
import type React from 'react'
import { useState } from 'react'
import type { CardType } from '../../api/cards/types.ts'
import TextContainer from '../../components/TextContainer.tsx'
import { TabSets } from '../../helpers/TabsHelpers.ts'
import type { RegionAndEcommBlockingInterface } from '../../interfaces/CardInterfaces.ts'
import TextKeys from '../../libs/TextKeys.ts'
import Tabs from '../../mynt-components/components/Tabs.tsx'
import CustomerCardTabOverviewAccountUsageLimits from './CustomerCardTabOverviewAccountUsageLimits.tsx'
import { CustomerCardTabOverviewSingleCardDetailsAudit } from './CustomerCardTabOverviewSingleCardDetailsAudit.tsx'
import { CustomerCardTabOverviewSingleCardDetailsCategory } from './CustomerCardTabOverviewSingleCardDetailsCategory.tsx'
import CustomerCardTabOverviewSingleCardDetailsPrivateCard from './CustomerCardTabOverviewSingleCardDetailsPrivateCard.tsx'
import CustomerCardTabOverviewSingleCardDetailsRegionAndEcommBlocking from './CustomerCardTabOverviewSingleCardDetailsRegionAndEcommBlocking.tsx'
import { CustomerCardTabOverviewSingleCardDetailsTokens } from './CustomerCardTabOverviewSingleCardDetailsTokens.tsx'

enum CardDetailsTabIndex {
  Overview = 0,
  UsageLimits = 1,
  RegionalBlocks = 2,
  Category = 3,
  Tokens = 4,
  Audit = 5,
}

interface CustomerCardTabOverviewSingleCardDetailsProps {
  customerId: string
  card?: {
    id: string
    internalId: string
    singleAmountLimit?: number
    cardType: CardType
  }
  personId: string
  onClose?: () => void
  onActiveTabChange?: (showExtraWideModal: boolean) => void
}

const CustomerCardTabOverviewSingleCardDetails: React.FC<CustomerCardTabOverviewSingleCardDetailsProps> = ({
  customerId,
  card: selectedCard,
  personId,
  onClose,
  onActiveTabChange,
}) => {
  const cardId = selectedCard?.id || ''

  const { data: card, isLoading: cardLoading } = usePrivateCustomerCard(customerId, cardId)
  const { data: cardPlastics = [], isLoading: cardPlasticsLoading } = usePrivateCustomerCardPlastics(customerId, cardId)

  const cardMutation = usePatchCard(customerId, cardId)

  const isLoading = cardLoading || cardPlasticsLoading

  const [activeTab, setActiveTab] = useState(0)

  const handleTabChange = (activeIndex: number) => {
    setActiveTab(activeIndex)
    onActiveTabChange?.(activeIndex === CardDetailsTabIndex.Audit)
  }

  const handleOnRegionalBlock = async (property: keyof RegionAndEcommBlockingInterface) => {
    if (!card?.regionAndEcommBlocking) return

    const value = card.regionAndEcommBlocking[property]

    await cardMutation.mutateAsync({
      regionAndEcommBlocking: {
        ...card.regionAndEcommBlocking,
        [property]: !value,
      },
    })
  }

  const tabs = [
    {
      id: 'overview',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Overview'} />,
      contentElement: (
        <CustomerCardTabOverviewSingleCardDetailsPrivateCard
          customerId={customerId}
          card={card}
          internalCardId={selectedCard?.internalId as string}
          plastics={cardPlastics}
          isLoading={isLoading}
          personId={personId}
        />
      ),
      enable: true,
    },
    {
      id: 'usage-limits',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Usage limits'} />,
      contentElement: (
        <div style={{ marginTop: -20 }}>
          <CustomerCardTabOverviewAccountUsageLimits
            isLoading={isLoading}
            customerId={customerId}
            internalCardId={selectedCard?.internalId as string}
            singleAmountLimit={selectedCard?.singleAmountLimit}
            usageLimits={card?.usageLimits || []}
          />
        </div>
      ),
      enable: true,
    },
    {
      id: 'regional-blocks',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Regional blocks'} />,
      contentElement: (
        <div style={{ marginTop: -30 }}>
          <CustomerCardTabOverviewSingleCardDetailsRegionAndEcommBlocking
            data={card?.regionAndEcommBlocking as any}
            onCheckboxClick={handleOnRegionalBlock as any}
            isCardLoading={cardLoading}
          />
        </div>
      ),
      enable: true,
    },
    {
      id: 'category',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Category'} />,
      contentElement: (
        <div style={{ marginTop: -10 }}>
          {selectedCard && (
            <CustomerCardTabOverviewSingleCardDetailsCategory
              personId={personId}
              customerId={customerId}
              cardDetails={{
                cardId: selectedCard.internalId as string,
                cardType: selectedCard.cardType,
              }}
              onClose={onClose!}
            />
          )}
        </div>
      ),
      enable: true,
    },
    {
      id: 'tokens',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Tokens'} />,
      contentElement: (
        <div style={{ marginTop: -10 }}>
          {selectedCard && (
            <CustomerCardTabOverviewSingleCardDetailsTokens customerId={customerId} cardId={selectedCard.internalId as string} />
          )}
        </div>
      ),
      enable: true,
    },
    {
      id: 'audit',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Audit'} />,
      contentElement: (
        <div style={{ marginTop: -10 }}>
          {selectedCard && (
            <CustomerCardTabOverviewSingleCardDetailsAudit customerId={customerId} cardId={selectedCard.internalId as string} />
          )}
        </div>
      ),
      enable: true,
    },
  ]

  return (
    <Tabs
      fullPage
      alwaysShowNavHeadings
      tabKeyNameSpace={TabSets.CUSTOMER_SCORING_BANKACCOUNT}
      tab={activeTab}
      onTabChange={handleTabChange}
      tabs={tabs}
    />
  )
}

export default CustomerCardTabOverviewSingleCardDetails
